// =====================================================
// Text Reveal Animations
// Reusable GSAP text reveal effects
// =====================================================

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * Split text into lines/words/chars for animation
 * @param {HTMLElement} element - The element to split
 * @param {string} type - 'words', 'chars', or 'lines'
 * @returns {HTMLElement[]} Array of split elements
 */
function splitText(element, type = "words") {
    const text = element.textContent;
    element.innerHTML = "";

    if (type === "chars") {
        const chars = text.split("");
        return chars.map(char => {
            const span = document.createElement("span");
            span.className = "char";
            span.textContent = char === " " ? "\u00A0" : char;
            span.style.display = "inline-block";
            element.appendChild(span);
            return span;
        });
    } else if (type === "words") {
        const words = text.split(" ");
        return words.map((word, i) => {
            const span = document.createElement("span");
            span.className = "word";
            span.textContent = word;
            span.style.display = "inline-block";
            element.appendChild(span);
            if (i < words.length - 1) {
                element.appendChild(document.createTextNode(" "));
            }
            return span;
        });
    }
    return [element];
}

/**
 * Reveal animation - text slides up from behind a mask (with reverse)
 * @param {string} selector - CSS selector for elements to animate
 */
export function initRevealAnimation(selector = "[data-reveal]") {
    const elements = document.querySelectorAll(selector);

    elements.forEach(element => {
        const type = element.dataset.reveal || "words";
        const stagger = parseFloat(element.dataset.stagger) || 0.05;
        const duration = parseFloat(element.dataset.duration) || 0.8;
        const delay = parseFloat(element.dataset.delay) || 0;

        // Wrap element for overflow hidden mask effect
        const wrapper = document.createElement("div");
        wrapper.className = "reveal-wrapper";
        wrapper.style.overflow = "hidden";
        wrapper.style.display = element.tagName === "SPAN" ? "inline-block" : "block";
        element.parentNode.insertBefore(wrapper, element);
        wrapper.appendChild(element);

        // Split text if needed
        let targets;
        if (type === "element") {
            targets = [element];
        } else {
            targets = splitText(element, type);
        }

        // Set initial state
        gsap.set(targets, {
            yPercent: 100,
            opacity: 0,
        });

        // Create scroll-triggered animation with reverse
        gsap.to(targets, {
            yPercent: 0,
            opacity: 1,
            duration: duration,
            stagger: stagger,
            delay: delay,
            ease: "power3.out",
            scrollTrigger: {
                trigger: wrapper,
                start: "top 85%",
                end: "top 20%",
                toggleActions: "play reverse play reverse",
            }
        });
    });
}

/**
 * Fade up animation - simple fade + slide up (with reverse)
 * @param {string} selector - CSS selector for elements to animate
 */
export function initFadeUpAnimation(selector = "[data-fade-up]") {
    const elements = document.querySelectorAll(selector);

    elements.forEach(element => {
        const duration = parseFloat(element.dataset.duration) || 0.8;
        const delay = parseFloat(element.dataset.delay) || 0;
        const distance = parseFloat(element.dataset.distance) || 40;

        gsap.set(element, {
            y: distance,
            opacity: 0,
        });

        gsap.to(element, {
            y: 0,
            opacity: 1,
            duration: duration,
            delay: delay,
            ease: "power2.out",
            scrollTrigger: {
                trigger: element,
                start: "top 85%",
                end: "top 20%",
                toggleActions: "play reverse play reverse",
            }
        });
    });
}

/**
 * Line reveal animation - reveals line by line (with reverse)
 * @param {string} selector - CSS selector for elements to animate
 */
export function initLineReveal(selector = "[data-line-reveal]") {
    const elements = document.querySelectorAll(selector);

    elements.forEach(element => {
        const lines = element.innerHTML.split("<br>");
        element.innerHTML = "";

        const lineElements = lines.map(line => {
            const wrapper = document.createElement("div");
            wrapper.className = "line-wrapper";
            wrapper.style.overflow = "hidden";

            const inner = document.createElement("div");
            inner.className = "line-inner";
            inner.innerHTML = line;

            wrapper.appendChild(inner);
            element.appendChild(wrapper);
            return inner;
        });

        gsap.set(lineElements, {
            yPercent: 100,
        });

        gsap.to(lineElements, {
            yPercent: 0,
            duration: 0.8,
            stagger: 0.1,
            ease: "power3.out",
            scrollTrigger: {
                trigger: element,
                start: "top 85%",
                end: "top 20%",
                toggleActions: "play reverse play reverse",
            }
        });
    });
}

/**
 * Initialize all text animations
 */
export function initTextAnimations() {
    initRevealAnimation();
    initFadeUpAnimation();
    initLineReveal();
}
